import React, { useEffect, useState, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import { Loader2, Eye, PlayCircle, CheckCircle2, Download, TrendingUp, FileSpreadsheet } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RANGES = [["today", "Aujourd'hui"], ["7d", "7 jours"], ["30d", "30 jours"], ["90d", "90 jours"], ["all", "Tout"], ["custom", "Personnalisé"]];

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl bg-white p-4 ring-1 ring-caneat-cream">
      <div className={`mb-2 flex h-9 w-9 items-center justify-center rounded-xl ${accent}`}><Icon className="h-5 w-5" strokeWidth={2.4} /></div>
      <p className="text-2xl font-extrabold text-caneat-ink">{value}</p>
      <p className="text-xs font-semibold text-caneat-muted">{label}</p>
    </div>
  );
}

export default function StatsPanel() {
  const { authHeaders, token } = useAuth();
  const [range, setRange] = useState("30d");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [machine, setMachine] = useState("all");
  const [machines, setMachines] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/admin/machines", { headers: authHeaders() }).then(({ data }) => setMachines(data)).catch(() => {}); }, [authHeaders]);

  const buildParams = useCallback(() => {
    const p = { range, machine_id: machine };
    if (range === "custom") { if (from) p.date_from = new Date(from).toISOString(); if (to) p.date_to = new Date(to + "T23:59:59").toISOString(); }
    return p;
  }, [range, from, to, machine]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/admin/stats", { params: buildParams(), headers: authHeaders() });
    setStats(data); setLoading(false);
  }, [buildParams, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const exportFile = (ext) => {
    const params = new URLSearchParams(buildParams());
    if (token) params.set("auth", token);
    window.open(`${API}/admin/survey/export.${ext}?${params.toString()}`, "_blank");
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="caneat-title text-3xl text-caneat-ink">Statistiques</h1>
        <div className="flex gap-2">
          <button onClick={() => exportFile("csv")} data-testid="export-csv-btn" className="flex items-center gap-2 rounded-full bg-caneat-ink px-3.5 py-2 text-sm font-bold text-white"><Download className="h-4 w-4" /> CSV</button>
          <button onClick={() => exportFile("xlsx")} data-testid="export-xlsx-btn" className="flex items-center gap-2 rounded-full bg-emerald-600 px-3.5 py-2 text-sm font-bold text-white"><FileSpreadsheet className="h-4 w-4" /> XLSX</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {RANGES.map(([k, label]) => (
          <button key={k} onClick={() => setRange(k)} data-testid={`range-${k}`}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold ${range === k ? "bg-caneat-orange text-white" : "bg-white text-caneat-muted ring-1 ring-caneat-cream"}`}>{label}</button>
        ))}
        <select value={machine} onChange={(e) => setMachine(e.target.value)} data-testid="stats-machine-filter"
          className="rounded-full border-2 border-caneat-cream bg-white px-3 py-1.5 text-xs font-bold text-caneat-ink outline-none">
          <option value="all">Tous les distributeurs</option>
          {machines.map((m) => <option key={m.id} value={m.machine_id}>{m.label}</option>)}
        </select>
      </div>

      {range === "custom" && (
        <div className="flex flex-wrap gap-3">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm" />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm" />
        </div>
      )}

      {loading || !stats ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-caneat-orange" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Kpi icon={Eye} label="Visites / scans" value={stats.visits} accent="bg-sky-100 text-sky-600" />
            <Kpi icon={PlayCircle} label="Sondages commencés" value={stats.surveys_started} accent="bg-amber-100 text-amber-600" />
            <Kpi icon={CheckCircle2} label="Sondages terminés" value={stats.surveys_completed} accent="bg-emerald-100 text-emerald-600" />
            <Kpi icon={TrendingUp} label="Taux de complétion" value={`${stats.completion_rate}%`} accent="bg-caneat-orange/15 text-caneat-orange" />
          </div>

          <div className="rounded-2xl bg-white p-4 ring-1 ring-caneat-cream" data-testid="responses-chart">
            <h3 className="caneat-title mb-1 text-lg text-caneat-ink">Réponses au sondage</h3>
            <p className="mb-4 text-xs font-semibold text-caneat-muted">{stats.total_responses} réponse(s) au total</p>
            {stats.total_responses === 0 ? <p className="py-8 text-center text-sm text-caneat-muted">Aucune réponse sur cette période.</p> : (
              <>
                <ResponsiveContainer width="100%" height={Math.max(160, stats.options.length * 46)}>
                  <BarChart data={stats.options.map((o) => ({ name: o.label?.fr || "—", count: o.count }))} layout="vertical" margin={{ left: 0, right: 16 }}>
                    <XAxis type="number" allowDecimals={false} hide />
                    <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fontWeight: 600 }} />
                    <Tooltip /><Bar dataKey="count" fill="#F3781E" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3 space-y-1.5">
                  {stats.options.map((o) => (
                    <div key={o.id} className="flex items-center justify-between text-sm" data-testid={`stat-option-${o.id}`}>
                      <span className="font-semibold text-caneat-ink">{o.label?.fr || "—"}</span>
                      <span className="font-bold text-caneat-orange">{o.count} · {o.pct}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="rounded-2xl bg-white p-4 ring-1 ring-caneat-cream">
            <h3 className="caneat-title mb-4 text-lg text-caneat-ink">Évolution des réponses</h3>
            {stats.timeseries.length === 0 ? <p className="py-6 text-center text-sm text-caneat-muted">Pas assez de données.</p> : (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={stats.timeseries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3E3D3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} /><Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#F3781E" strokeWidth={3} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="rounded-2xl bg-white p-4 ring-1 ring-caneat-cream">
            <h3 className="caneat-title mb-3 text-lg text-caneat-ink">Produits les plus consultés</h3>
            {stats.top_products.length === 0 ? <p className="py-4 text-center text-sm text-caneat-muted">Aucune consultation sur cette période.</p> : (
              <div className="space-y-2">
                {stats.top_products.map((p, i) => (
                  <div key={p.slug} className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-caneat-ink">{i + 1}. {p.slug}</span>
                    <span className="font-bold text-caneat-orange">{p.views} vues</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
